import { CreditCard, ExternalLink } from 'lucide-react'

import { Banner } from 'components/reusable/Banner'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Badge } from 'slab'
import { useGetBrokersUnprocessedEnrollmentsId } from 'utils/web_api'

import EnrollmentActionsPill from './EnrollmentActionsPill'
import EnrollmentAutopayPill from './EnrollmentAutopayPill'
import EnrollmentEventPill from './EnrollmentEventPill'
import EnrollmentPaymentStatusPill from './EnrollmentPaymentStatusPill'
import EnrollmentStatusPill from './EnrollmentStatusPill'
import EnrollmentSubmissionMethodPill from './EnrollmentSubmissionMethodPill'
import TaskPill from './TaskPill'
import {
  type EnrollmentAutopayState,
  type EnrollmentEventType,
  type EnrollmentStatus,
  type SubmissionMethod,
} from './types'

type BrokersEnrollmentStatusOutputProps = {
  handleActionError: () => void
  enrollmentId: string
  memberId: string
  addLinkToTransactionsPage?: boolean
  hasEnrollmentPayments?: boolean
}

const BrokersEnrollmentStatusOutput = ({
  handleActionError,
  enrollmentId,
  memberId,
  addLinkToTransactionsPage = true,
  hasEnrollmentPayments = false,
}: BrokersEnrollmentStatusOutputProps) => {
  const {
    data: enrollment,
    isLoading: isEnrollmentLoading,
    isError: isEnrollmentError,
  } = useGetBrokersUnprocessedEnrollmentsId(enrollmentId || '')

  if (isEnrollmentLoading) {
    return (
      <div className='flex justify-start items-center space-x-2 flex-wrap'>
        <SkeletonItem widthClass='w-[150px] h-6' />
        <SkeletonItem widthClass='w-[150px] h-6' />
        <SkeletonItem widthClass='w-[150px] h-6' />
      </div>
    )
  }

  if (isEnrollmentError) {
    return (
      <Banner type='critical' titleText='Error loading enrollment'>
        Please retry and reach out to support if you continue to have this issue
      </Banner>
    )
  }

  if (!enrollment) return null

  return (
    <div className='flex flex-wrap gap-2'>
      <EnrollmentActionsPill
        enrollmentId={enrollmentId}
        status={enrollment?.merged_status as EnrollmentStatus | undefined}
        submissionMethod={
          enrollment?.submission_method as SubmissionMethod | undefined
        }
        autopayState={
          enrollment?.autopay_state as EnrollmentAutopayState | undefined
        }
        coverageStartDate={enrollment?.coverage_start_date}
        onError={handleActionError}
        hasEnrollmentPayments={hasEnrollmentPayments}
      />
      <EnrollmentStatusPill
        status={enrollment?.merged_status as EnrollmentStatus | undefined}
        submissionMethod={
          enrollment?.submission_method as SubmissionMethod | undefined
        }
      />
      <EnrollmentSubmissionMethodPill
        submissionMethod={
          enrollment?.submission_method as SubmissionMethod | undefined
        }
        assigneeName={enrollment?.assignee_name}
      />
      {enrollment?.tasks?.map((task, index) => (
        <TaskPill key={index} task={task} />
      ))}
      <EnrollmentAutopayPill
        autopayState={
          enrollment?.autopay_state as EnrollmentAutopayState | undefined
        }
      />
      {enrollment?.payment_standing && (
        <EnrollmentPaymentStatusPill
          memberId={memberId}
          paymentStatus={enrollment?.payment_standing?.status as string}
          paidThroughMonthName={
            enrollment?.payment_standing?.paid_through_month_name as
              | string
              | undefined
          }
          addPaymentStatusPrefix
          addLinkToTransactionsPage={addLinkToTransactionsPage}
        />
      )}
      {enrollment.enrollment_event_type && (
        <EnrollmentEventPill
          enrollmentType={
            enrollment.enrollment_event_type as EnrollmentEventType
          }
          enrollmentSubtype={enrollment.enrollment_event_subtype}
        />
      )}
      {enrollment.plan_hsa_eligible && <Badge>HSA-eligible</Badge>}
      {enrollment.payment_method_url && (
        <Badge
          leftIcon={CreditCard}
          type='info'
          onActionClick={() => {
            window.open(enrollment.payment_method_url, '_blank')
          }}
          actionIcon={ExternalLink}
        >
          {enrollment.payment_method_type}
        </Badge>
      )}
    </div>
  )
}

export default BrokersEnrollmentStatusOutput
